import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  ArrowDownRight,
  Activity,
  Repeat,
  FileText,
  DollarSign,
  Clock,
  AlertCircle,
  AlertTriangle,
  Plus,
  RefreshCw,
  ArrowRight,
  Download,
  ShieldCheck,
  Receipt,
  ChevronRight,
  Star,
  Code2,
  Webhook,
  Globe,
  BookOpen,
} from 'lucide-react';
import '../styles/responsive/pages/Home.css';
import { useTheme } from '../context/ThemeContext';
import { formatUSD, formatCount } from '../utils/formatAmount';
import { fetchWithAuth } from '../utils/api';
import { deriveCounts } from '../utils/stats';
import { useGetOnboardingQuery } from '../store/api/onboardingApi';
import Skeleton from '../components/common/Skeleton';
import StatCard from '../components/common/StatCard';
import { RecentTransactionsTable, RecentInvoicesTable } from '../components/common/RecentTables';
import usePremiumUpsell from '../hooks/usePremiumUpsell';
import PremiumUpsellModal from '../components/common/PremiumUpsellModal';

// P5: a single large succeeded payment above this USD threshold triggers the
// contextual Premium upsell (once per browser session).
const LARGE_PAYMENT_UPSELL_USD = 2000;

// DSH-05: period options driving the dashboard. Persisted across visits.
const PERIODS = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 },
  { key: 'all', label: 'All', days: null },
];

// Period key -> the `range` the /transactions/summary endpoint expects. Kept
// beside PERIODS so adding one without wiring it up is obvious.
const PERIOD_TO_RANGE = {
  today: '1d',
  '7d': '7d',
  '30d': '30d',
  all: 'all',
};

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Onboarding steps that make up the "Finish setup" progress bar.
const SETUP_ORDER = ['started', 'business_done', 'usecases_done', 'activation_done', 'payment_done', 'completed'];
const SETUP_STEPS = ['business_done', 'activation_done', 'payment_done', 'completed'];

const relativeTime = (then, now) => {
  if (!then) return '';
  const s = Math.max(0, Math.round((now - then) / 1000));
  if (s < 5) return 'just now';
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

// The recent transactions / invoices tables, their status tones and labels
// live in components/common/RecentTables (shared with the Balance page).

// DSH-06: dependency-free SVG area chart (no charting library added).
// Sparkline for the overview cards. Baselined at zero, not at the series
// minimum: scaling to the range made a flat week of real volume look identical
// to a flat week of nothing.
const MiniSpark = ({ points, tone }) => {
  if (!points || points.length < 2) return null;
  const w = 100;
  const h = 30;
  const max = Math.max(...points, 0) || 1;
  const xy = points.map((v, i) => [
    (i / (points.length - 1)) * w,
    h - 2 - (Math.max(v, 0) / max) * (h - 8),
  ]);
  let line = `M ${xy[0][0]},${xy[0][1]}`;
  for (let i = 1; i < xy.length; i += 1) {
    const [px, py] = xy[i - 1];
    const [cx, cy] = xy[i];
    const mx = (px + cx) / 2;
    line += ` C ${mx},${py} ${mx},${cy} ${cx},${cy}`;
  }
  return (
    <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className={`mini-spark tone-${tone}`} aria-hidden="true">
      <path d={`${line} L ${w},${h} L 0,${h} Z`} className="mini-spark__area" />
      <path d={line} className="mini-spark__line" />
    </svg>
  );
};

/* Rounds a maximum up to a readable axis top (1 / 2 / 2.5 / 5 x 10^n), so the
   Y labels land on values a person would actually choose. Without it the top
   tick is whatever the largest payment happened to be. */
const niceCeil = (value) => {
  if (!Number.isFinite(value) || value <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(value)));
  const norm = value / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 2.5 ? 2.5 : norm <= 5 ? 5 : 10;
  return step * mag;
};

/* Compact axis labels: 12500 -> 12.5k. Only used on the Y axis, never on a
   figure the user has to reconcile against a real total.
 *
 * The unit is chosen from the axis TOP, not from each value, for two reasons a
 * per-value version got wrong: an axis topping out at 2.5m produced a ladder
 * that mixed k and m ("625k, 1.3m"), and a small axis rounded every tick to an
 * integer, so a merchant whose largest bucket was $0.40 saw "0, 0, 0, 0, 1"
 * instead of "0, 0.13, 0.25, 0.38, 0.5". */
const axisTick = (v, top) => {
  if (v === 0) return '0';
  if (top >= 1_000_000) return `${(v / 1_000_000).toFixed(v % 1_000_000 ? 1 : 0)}m`;
  // Keyed off the tick STEP, not the top. A $1,000 axis steps by 250, and
  // abbreviating those gave "0.3k, 0.5k, 0.8k" where plain numbers are both
  // shorter and exact; k only earns its place once a step is itself >= 1000.
  if (top / 4 >= 1000) return `${(v / 1000).toFixed(v % 1000 ? 1 : 0)}k`;
  if (top >= 10) return String(Math.round(v));
  // Sub-$10 axis: keep the decimals, drop trailing zeros via parseFloat.
  return String(parseFloat(v.toFixed(2)));
};

/* Catmull-Rom -> cubic Bezier. Turns the bucket points into a flowing curve
 * instead of the straight polyline the sharp version drew.
 *
 * The control points are CLAMPED to the plot box, and that is a correctness
 * measure rather than cosmetic: a Catmull-Rom spline overshoots around a spike,
 * so a zero bucket beside a large one would dip the curve below the zero line
 * and imply negative revenue. A cubic Bezier is contained by the convex hull of
 * its four control points; the two endpoints are real data and therefore
 * already inside the box, so clamping the two handles guarantees the whole
 * curve stays in range.
 */
const smoothPath = (pts, yMin, yMax) => {
  if (!pts.length) return '';
  if (pts.length === 1) return `M${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
  const clamp = (y) => Math.min(yMax, Math.max(yMin, y));
  // Catmull-Rom tension / 6. Below ~1 the curve hugs the data more tightly;
  // 0.9 reads as smooth without inventing bumps between buckets.
  const T = 0.9 / 6;
  let d = `M${pts[0][0].toFixed(2)},${pts[0][1].toFixed(2)}`;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) * T;
    const c1y = clamp(p1[1] + (p2[1] - p0[1]) * T);
    const c2x = p2[0] - (p3[0] - p1[0]) * T;
    const c2y = clamp(p2[1] - (p3[1] - p1[1]) * T);
    d += ` C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
  }
  return d;
};

/* Three-series trend chart. Dependency-free SVG -- no charting library is
 * added -- sized as a compact panel rather than a hero graphic.
 *
 * All three curves are USD and share one Y axis, which is what makes them
 * comparable. `start` and `slot` come from the same aggregation that produced
 * the series, so every label is derived from the real bucket boundaries.
 */
const TrendChart = ({ series, start, slot, period }) => {
  const [hover, setHover] = useState(null);
  const svgRef = useRef(null);

  // viewBox units, not pixels: the SVG scales to the container, so the tooltip
  // can be placed as a percentage of these.
  //
  // The width is deliberately large relative to the height. The SVG is
  // width:100% / height:auto, so its rendered height is (container / W) * H —
  // at 760 wide that made a full-width panel about 410px tall, which is why the
  // chart towered over the page with most of it empty. A wider box lands the
  // same drawing at roughly half the height without distorting anything.
  const W = 1500;
  const H = 168;
  const PAD_L = 56;
  const PAD_B = 26;
  const PAD_T = 12;
  const PAD_R = 16;

  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;
  const yTop = PAD_T;
  const yBottom = PAD_T + plotH;

  const n = series[0]?.values.length || 0;
  // One shared axis across all three, so the curves can be read against each
  // other rather than each being scaled to its own maximum.
  const top = niceCeil(Math.max(0, ...series.flatMap((sr) => sr.values)));

  const xAt = (i) => PAD_L + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const yAt = (v) => yTop + plotH - (v / top) * plotH;

  const ticks = [0, 0.5, 1].map((f) => f * top);

  const hourly = period === 'today';
  const labelAt = (i) => {
    const d = new Date(start + i * slot);
    return hourly
      ? d.toLocaleTimeString('en-US', { hour: 'numeric' })
      : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const labelStep = Math.max(1, Math.ceil(n / 6));
  const xLabels = [];
  for (let i = 0; i < n; i += labelStep) xLabels.push(i);
  if (n && xLabels[xLabels.length - 1] !== n - 1) xLabels.push(n - 1);

  const onMove = (e) => {
    const svg = svgRef.current;
    if (!svg || !n) return;
    const rect = svg.getBoundingClientRect();
    const vx = ((e.clientX - rect.left) / rect.width) * W;
    const frac = (vx - PAD_L) / plotW;
    setHover(Math.min(n - 1, Math.max(0, Math.round(frac * (n - 1)))));
  };

  return (
    <div className="trend-chart">
      <svg
        ref={svgRef}
        className="trend-chart__svg"
        viewBox={`0 0 ${W} ${H}`}
        role="img"
        aria-label="Payments, payouts and invoiced amounts over the selected period"
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          {/* Only the primary curve is filled. Three stacked translucent areas
              muddy into an unreadable wash, so the other two stay as lines. */}
          <linearGradient id="trendFillPayments" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--trend-c-payments)" stopOpacity="0.26" />
            <stop offset="100%" stopColor="var(--trend-c-payments)" stopOpacity="0" />
          </linearGradient>
          <filter id="trendGlow" x="-8%" y="-30%" width="116%" height="160%">
            <feDropShadow dy="2.5" stdDeviation="2.5" floodOpacity="0.20" />
          </filter>
        </defs>

        {ticks.map((t) => (
          <g key={t}>
            <line className="trend-chart__grid" x1={PAD_L} x2={W - PAD_R} y1={yAt(t)} y2={yAt(t)} />
            <text className="trend-chart__ytick" x={PAD_L - 9} y={yAt(t) + 3.5} textAnchor="end">
              {axisTick(t, top)}
            </text>
          </g>
        ))}

        {xLabels.map((i) => (
          <text
            key={i}
            className="trend-chart__xtick"
            x={xAt(i)}
            y={H - 5}
            textAnchor={i === 0 ? 'start' : i === n - 1 ? 'end' : 'middle'}
          >
            {labelAt(i)}
          </text>
        ))}

        {series.map((sr) => {
          const pts = sr.values.map((v, i) => [xAt(i), yAt(v)]);
          const line = smoothPath(pts, yTop, yBottom);
          return (
            <g key={sr.key} style={{ '--trend-c': `var(--trend-c-${sr.key})` }}>
              {sr.fill && (
                <path
                  className="trend-chart__area"
                  d={`${line} L${xAt(n - 1).toFixed(2)},${yBottom} L${xAt(0).toFixed(2)},${yBottom} Z`}
                  fill={`url(#trendFill${sr.key.charAt(0).toUpperCase()}${sr.key.slice(1)})`}
                />
              )}
              <path className="trend-chart__line" d={line} filter="url(#trendGlow)" />
            </g>
          );
        })}

        {hover != null && (
          <g>
            <line
              className="trend-chart__cursor"
              x1={xAt(hover)}
              x2={xAt(hover)}
              y1={yTop}
              y2={yBottom}
            />
            {series.map((sr) => (
              <circle
                key={sr.key}
                className="trend-chart__dot"
                style={{ '--trend-c': `var(--trend-c-${sr.key})` }}
                cx={xAt(hover)}
                cy={yAt(sr.values[hover])}
                r="6"
              />
            ))}
          </g>
        )}
      </svg>

      {hover != null && (
        <div
          /* Opens AWAY from the nearer edge. It always opened to the left of
             the point, so hovering the first hours of the day pushed it off
             the card's left edge, where the card clipped it. On the left half
             it now opens to the right, on the right half to the left. */
          className={`trend-chart__tip ${
            xAt(hover) / W < 0.5 ? 'trend-chart__tip--right' : 'trend-chart__tip--left'
          }`}
          /* Anchored on x only. Pinning to a curve's y would make the tooltip
             jump between three different heights as the pointer moves. */
          style={{ left: `${(xAt(hover) / W) * 100}%` }}
        >
          <span className="trend-chart__tip-when">{labelAt(hover)}</span>
          {series.map((sr) => (
            <span key={sr.key} className="trend-chart__tip-row">
              <i style={{ background: `var(--trend-c-${sr.key})` }} />
              {sr.label}
              <b>${formatUSD(sr.values[hover])}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const Home = () => {
  const { user } = useSelector((state) => state.auth);
  const { mode } = useTheme();
  const navigate = useNavigate();
  const upsell = usePremiumUpsell();

  const [balanceStats, setBalanceStats] = useState(null);
  const [transactionStats, setTransactionStats] = useState(null);
  const [recentTransactions, setRecentTransactions] = useState([]);
  const [recentInvoices, setRecentInvoices] = useState([]);
  // Overdue is fetched separately rather than filtered out of the 5 recent
  // invoices: an account can hold hundreds of overdue invoices and still have
  // none in its newest 5, which made the Overdue toggle look broken.
  const [overdueInvoiceList, setOverdueInvoiceList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  // DSH-04/07: withdrawable balance summary.
  const [myBalance, setMyBalance] = useState(null);
  // DSH-06: succeeded transactions used to build the revenue chart client-side.
  const [chartTxns, setChartTxns] = useState([]);
  // Companion series for the trend chart: completed payouts and raised invoices.
  const [chartPayouts, setChartPayouts] = useState([]);
  const [chartInvoices, setChartInvoices] = useState([]);
  // DSH-05: selected period, persisted across visits (default 7d to match the header label).
  const [period, setPeriod] = useState(() => localStorage.getItem('dashboardPeriod') || '7d');
  // Period totals, deltas, daily series and the asset split, from the same
  // endpoint the transactions page uses. None of it is derivable here: the
  // dashboard loads at most 500 recent rows, and a "vs. previous period" delta
  // needs a period it never fetches.
  const [summary, setSummary] = useState(null);
  // "Updated Xs ago" — real timestamp of the last successful fetch + a ticking clock.
  const [lastUpdated, setLastUpdated] = useState(null);
  const [nowTick, setNowTick] = useState(() => 0);
  // Recent-invoices quick filter (ties the overdue attention strip to the list).
  const [invoiceFilter, setInvoiceFilter] = useState('all');

  // DSH-03: onboarding status powers the setup banner.
  const { data: onboarding } = useGetOnboardingQuery();

  const handlePeriodChange = (key) => {
    setPeriod(key);
    localStorage.setItem('dashboardPeriod', key);
  };

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    try {
      setFetchError(false);
      if (isRefresh) setRefreshing(true);
      else setLoading(true);

      const isTestnet = mode === 'test' ? 'true' : 'false';
      // Same noise rules as the Transactions page: hide dust, and hide money
      // nobody asked for in a token the merchant does not accept. Applied to the
      // stats as well as the list, so "Showing 5 of N" counts the same rows.
      const txNoise = 'hideDust=true&hideUntrackedUnsupported=true';

      const [balanceStatsRes, transactionStatsRes, transactionsRes, invoicesRes, overdueRes, balanceRes, chartRes, payoutSeriesRes, invoiceSeriesRes] = await Promise.all([
        fetchWithAuth(`${API_BASE_URL}/invoices/balance-stats?isTestnet=${isTestnet}`),
        fetchWithAuth(`${API_BASE_URL}/transactions/stats?isTestnet=${isTestnet}&${txNoise}`),
        fetchWithAuth(`${API_BASE_URL}/transactions?limit=5&sortBy=created_at&sortOrder=DESC&isTestnet=${isTestnet}&${txNoise}`),
        fetchWithAuth(`${API_BASE_URL}/invoices?limit=5&sortBy=created_at&sortOrder=DESC&isTestnet=${isTestnet}`),
        // Oldest due first, so the toggle leads with the most overdue.
        fetchWithAuth(`${API_BASE_URL}/invoices?status=overdue&limit=5&sortBy=due_date&sortOrder=ASC&isTestnet=${isTestnet}`),
        fetchWithAuth(`${API_BASE_URL}/payouts/my-balance?isTestnet=${isTestnet}`),
        // Succeeded transactions for the revenue chart (aggregated by day client-side).
        fetchWithAuth(`${API_BASE_URL}/transactions?limit=500&status=succeeded&type=payment&sortBy=created_at&sortOrder=DESC&isTestnet=${isTestnet}`),
        // Completed payouts for the Payouts curve: money actually sent, which
        // is what makes it comparable with payments received. Requests that are
        // still pending or were rejected are not money out.
        fetchWithAuth(`${API_BASE_URL}/payouts/my-requests?page=1&limit=500&status=completed&sortBy=created_at&sortOrder=DESC&isTestnet=${isTestnet}`),
        // Invoices for the Invoiced curve. Deliberately unfiltered by status
        // and bucketed by createdAt below, so the curve is "amount billed",
        // which is distinct from payments received. Filtering to paid invoices
        // would just re-plot a subset of the payments curve.
        fetchWithAuth(`${API_BASE_URL}/invoices?limit=500&sortBy=created_at&sortOrder=DESC&isTestnet=${isTestnet}`),
      ]);

      const [balanceStatsData, transactionStatsData, transactionsData, invoicesData, overdueData, balanceData, chartData, payoutSeriesData, invoiceSeriesData] = await Promise.all([
        balanceStatsRes.json(),
        transactionStatsRes.json(),
        transactionsRes.json(),
        invoicesRes.json(),
        overdueRes.json(),
        balanceRes.json(),
        chartRes.json(),
        payoutSeriesRes.json(),
        invoiceSeriesRes.json(),
      ]);

      if (balanceStatsData.success) setBalanceStats(balanceStatsData.data);
      if (transactionStatsData.success) setTransactionStats(transactionStatsData.data);
      if (transactionsData.success) setRecentTransactions(transactionsData.data?.transactions || []);
      if (invoicesData.success) setRecentInvoices(invoicesData.data?.invoices || []);
      if (overdueData.success) setOverdueInvoiceList(overdueData.data?.invoices || []);
      if (balanceData.success) setMyBalance(balanceData.data);
      if (chartData.success) setChartTxns(chartData.data?.transactions || []);
      if (payoutSeriesData.success) setChartPayouts(payoutSeriesData.data?.requests || []);
      if (invoiceSeriesData.success) setChartInvoices(invoiceSeriesData.data?.invoices || []);
      setLastUpdated(Date.now());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      setFetchError(true);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [mode]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Re-render the "Updated Xs ago" label every 10s without refetching.
  useEffect(() => {
    const id = setInterval(() => setNowTick(Date.now()), 10000);
    return () => clearInterval(id);
  }, []);

  // P5: if the merchant has a large succeeded payment, nudge them toward Premium
  // (once per session). Reuses the already-loaded succeeded-payment set.
  useEffect(() => {
    if (!chartTxns.length) return;
    const hasLarge = chartTxns.some(
      (t) => (parseFloat(t.amountUsd ?? t.amount_usd ?? 0) || 0) > LARGE_PAYMENT_UPSELL_USD,
    );
    if (hasLarge) {
      upsell.maybeShow('large_payment', {
        context: `You're processing payments over $${LARGE_PAYMENT_UPSELL_USD.toLocaleString()}. Premium's 1% fee saves you more on every settlement.`,
      });
    }
  }, [chartTxns, upsell]);

  const getUserName = () => {
    if (!user) return 'User';
    return user.firstName || user.first_name || 'User';
  };

  const counts = deriveCounts(balanceStats);

  const totalRevenue = (balanceStats?.invoices?.totalPaid || 0) + (balanceStats?.sessions?.totalPaid || 0);
  const overdueInvoices = counts.invoice.overdueCount;

  const succeededCount = transactionStats?.succeeded || 0;
  const totalAttempts = transactionStats?.all || 0;
  const successRate = totalAttempts > 0 ? Math.round((succeededCount / totalAttempts) * 100) : 0;

  const availableBalance = myBalance?.availableBalance || 0;
  const pendingPayout = myBalance?.pendingPayoutAmount || 0;

  // KPI metrics owned only by the header row (Available/Pending live in the
  // balance panel, so the tiles surface invoice health instead).
  const overdueAmount = balanceStats?.totalOverdueAmount || 0;
  const totalInvoices = counts.invoice.total;
  const paidInvoices = counts.invoice.paidCount;

  // DSH-03: the dashboard is lifecycle-aware. No successful payment yet -> Activation mode.
  const hasSucceededPayment = succeededCount > 0 || totalRevenue > 0;
  const isLive = mode === 'live';

  // Setup progress from onboarding status.
  const setupReached = (s) => SETUP_ORDER.indexOf(onboarding?.onboarding_status || 'started') >= SETUP_ORDER.indexOf(s);
  const setupDone = SETUP_STEPS.filter(setupReached).length;
  const showSetup = onboarding?.onboarding_status !== 'completed';

  // DSH-06: aggregate succeeded payments into a daily revenue series + period totals.
  const activePeriod = PERIODS.find((p) => p.key === period) || PERIODS[1];
  const {
    chartSeries,
    periodRevenue,
    periodCount,
    chartStart,
    chartSlot,
    payoutSeries,
    invoiceSeries,
    payoutTotal,
    invoiceTotal,
  } = (() => {
    const days = activePeriod.days;
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;

    // Calendar-day boundaries, matching /transactions/summary.
    //
    // This used to be a rolling window: `start = now - days * dayMs`. "Today"
    // therefore meant "the last 24 hours", so a payment taken at 11pm yesterday
    // counted as today's, and every range disagreed with the KPI cards above it
    // -- the cards count whole days from midnight, the chart counted backwards
    // from the current clock time. Two different answers from one button.
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);

    // Today as a single bucket is one data point, which cannot draw a line.
    // Hourly buckets give it a shape. Totals are bucket-count independent, so
    // periodRevenue and periodCount are unaffected either way.
    let start;
    let span;
    let bucketCount;

    if (days === 1) {
      start = startOfToday.getTime();
      span = dayMs;
      bucketCount = 24;
    } else if (days) {
      start = startOfToday.getTime() - (days - 1) * dayMs;
      span = days * dayMs;
      bucketCount = Math.min(days, 30);
    } else {
      // All time: from the day of the earliest loaded payment.
      const times = chartTxns
        .map((t) => new Date(t.createdAt || t.created_at).getTime())
        .filter((n) => Number.isFinite(n));
      const earliest = times.length ? Math.min(...times) : now - 29 * dayMs;
      const earliestDay = new Date(earliest);
      earliestDay.setHours(0, 0, 0, 0);
      start = earliestDay.getTime();
      span = Math.max(startOfToday.getTime() + dayMs - start, dayMs);
      bucketCount = Math.min(Math.max(Math.round(span / dayMs), 1), 30);
    }
    const buckets = Array.from({ length: bucketCount }, () => 0);
    const payoutBuckets = Array.from({ length: bucketCount }, () => 0);
    const invoiceBuckets = Array.from({ length: bucketCount }, () => 0);
    const slot = span / bucketCount;
    let total = 0;
    let count = 0;
    let payoutTotal = 0;
    let invoiceTotal = 0;

    // Shared bucketing so all three curves land on identical boundaries; a
    // per-series version would let them drift and make the chart lie.
    const bucketIndex = (ts) => {
      if (!Number.isFinite(ts) || ts < start) return -1;
      return Math.min(bucketCount - 1, Math.max(0, Math.floor((ts - start) / slot)));
    };

    chartTxns.forEach((t) => {
      const ts = new Date(t.createdAt || t.created_at).getTime();
      if (!ts || ts < start) return;
      const idx = Math.min(bucketCount - 1, Math.max(0, Math.floor((ts - start) / slot)));
      const usd = parseFloat(t.amountUsd ?? t.amount_usd ?? 0) || 0;
      buckets[idx] += usd;
      total += usd;
      count += 1;
    });

    // Payout `amount` is already USD (the Payouts table renders it with a $).
    chartPayouts.forEach((r) => {
      const idx = bucketIndex(new Date(r.createdAt || r.created_at).getTime());
      if (idx < 0) return;
      const usd = parseFloat(r.amount ?? 0) || 0;
      payoutBuckets[idx] += usd;
      payoutTotal += usd;
    });

    // Bucketed by createdAt, so this is amount BILLED in the period, not
    // collected -- collected is already the payments curve.
    chartInvoices.forEach((inv) => {
      const idx = bucketIndex(new Date(inv.createdAt || inv.created_at).getTime());
      if (idx < 0) return;
      const usd = parseFloat(inv.amount ?? 0) || 0;
      invoiceBuckets[idx] += usd;
      invoiceTotal += usd;
    });
    // start/slot are returned so the chart can label its axis from the real
    // bucket boundaries instead of re-deriving them and drifting.
    return {
      chartSeries: buckets,
      periodRevenue: total,
      periodCount: count,
      chartStart: start,
      chartSlot: slot,
      payoutSeries: payoutBuckets,
      invoiceSeries: invoiceBuckets,
      payoutTotal,
      invoiceTotal,
    };
  })();

  /* Revenue in the last 24h against the 24h before it.
   *
   * This is deliberately a REVENUE delta, not a balance delta, and is labelled
   * as such in the UI. A balance change would need historical balance
   * snapshots, and none exist -- /payouts/my-balance returns only the current
   * figures -- so there is no honest way to produce one here.
   *
   * Returns null rather than 0% when the earlier window is empty: with no base
   * to compare against a percentage is undefined, and showing "+100%" or "0%"
   * for a merchant's first day of trading would be inventing a trend. */
  const revenue24h = (() => {
    const dayMs = 24 * 60 * 60 * 1000;
    const now = Date.now();
    let current = 0;
    let previous = 0;
    chartTxns.forEach((t) => {
      const ts = new Date(t.createdAt || t.created_at).getTime();
      if (!Number.isFinite(ts)) return;
      const usd = parseFloat(t.amountUsd ?? t.amount_usd ?? 0) || 0;
      const age = now - ts;
      if (age < 0) return;
      if (age <= dayMs) current += usd;
      else if (age <= dayMs * 2) previous += usd;
    });
    if (previous <= 0) return { current, pct: null };
    return { current, pct: ((current - previous) / previous) * 100 };
  })();

  /* The three curves. Labels are the user-facing names; the `hint` spells out
   * what each one actually measures, because two of them are easy to misread:
   * Payouts counts COMPLETED requests only (money genuinely sent, not money
   * requested), and Invoices is bucketed by creation date, so it is the amount
   * BILLED rather than collected -- collected is already the payments curve. */
  const trendSeries = [
    {
      key: 'payments',
      label: 'Total payments',
      hint: 'Succeeded payments received',
      values: chartSeries,
      total: periodRevenue,
      fill: true,
    },
    {
      key: 'payouts',
      label: 'Payouts',
      hint: 'Completed payouts sent',
      values: payoutSeries,
      total: payoutTotal,
    },
    {
      key: 'invoices',
      label: 'Invoices',
      hint: 'Amount invoiced',
      values: invoiceSeries,
      total: invoiceTotal,
    },
  ];

  const periodLabel = activePeriod.label === 'All' ? 'All time' : activePeriod.label;
  const hasChartData =
    chartSeries.some((v) => v > 0) ||
    payoutSeries.some((v) => v > 0) ||
    invoiceSeries.some((v) => v > 0);

  const filteredRecentInvoices = invoiceFilter === 'overdue' ? overdueInvoiceList : recentInvoices;

  // Export the currently-loaded recent transactions as CSV (no backend export endpoint exists).
  const handleExport = () => {
    const rows = [
      ['Date', 'Currency', 'Amount', 'Amount (USD)', 'Status'],
      ...recentTransactions.map((t) => [
        t.createdAt || '',
        t.currency || '',
        t.amount ?? '',
        t.amountUsd ?? '',
        t.status || '',
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `maxelpay-transactions-${mode}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // KPI tiles. Nothing is called a "payment" unless money arrived.
  //
  // The selected period is passed straight through. This used to map Today and
  // All onto '7d' — the nearest range the endpoint understood — so pressing
  // either left the cards showing a week of figures and only 30d appeared to do
  // anything. The endpoint now understands '1d' and 'all' directly.
  const summaryRange = PERIOD_TO_RANGE[period] || '7d';

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const isTestnet = mode === 'test';
        const response = await fetchWithAuth(
          `${API_BASE_URL}/transactions/summary?range=${summaryRange}` +
            `&isTestnet=${isTestnet}&hideDust=true&hideUntrackedUnsupported=true`
        );
        const json = await response.json();
        if (!cancelled && json.success) setSummary(json.data);
      } catch {
        if (!cancelled) setSummary(null);
      }
    })();
    return () => { cancelled = true; };
  }, [user, mode, summaryRange]);

  // "vs. previous 1 days" reads as a bug. All-time has nothing before it at all,
  // and the endpoint says so with `comparable: false`, in which case no delta is
  // rendered and this is never used.
  const comparisonLabel =
    summary?.days === 1 ? 'vs. yesterday' : `vs. previous ${summary?.days || 7} days`;

  const sumCur = summary?.current;
  const sumInv = summary?.invoices;
  const volumeSeries = (summary?.daily || []).map((d) => d.volumeUsd);
  const countSeries = (summary?.daily || []).map((d) => d.count);

  const kpis = [
    {
      key: 'revenue',
      tone: 'ok',
      label: 'Total Volume',
      icon: DollarSign,
      value: `$${formatUSD(sumCur?.volumeUsd ?? periodRevenue)}`,
      delta: summary?.deltas?.volumeUsd ?? null,
      sub: `${formatCount(sumCur?.count ?? periodCount)} payments`,
      series: volumeSeries,
    },
    {
      key: 'transactions',
      tone: 'info',
      label: 'Transactions',
      icon: Repeat,
      value: formatCount(sumCur?.count ?? periodCount),
      delta: summary?.deltas?.count ?? null,
      sub: `${formatCount(sumCur?.succeeded ?? 0)} succeeded`,
      series: countSeries,
    },
    {
      key: 'outstanding',
      tone: 'warn',
      label: 'Outstanding',
      icon: AlertTriangle,
      value: `$${formatUSD(overdueAmount)}`,
      delta: sumInv?.deltas?.outstanding ?? null,
      sub: overdueInvoices > 0
        ? `${formatCount(overdueInvoices)} overdue ${overdueInvoices === 1 ? 'invoice' : 'invoices'}`
        : 'Nothing overdue',
      series: volumeSeries,
    },
    {
      key: 'invoices',
      tone: 'brand',
      label: 'Invoices',
      icon: FileText,
      value: formatCount(totalInvoices),
      delta: sumInv?.deltas?.count ?? null,
      sub: totalInvoices > 0 ? `${formatCount(paidInvoices)} paid` : 'No invoices yet',
      series: countSeries,
    },
    {
      key: 'success',
      tone: 'info',
      label: 'Success Rate',
      icon: Activity,
      value: sumCur?.successRate != null
        ? `${sumCur.successRate.toFixed(1)}%`
        : (totalAttempts > 0 ? `${successRate}%` : '—'),
      delta: summary?.deltas?.successRate ?? null,
      sub: totalAttempts > 0
        ? `${formatCount(succeededCount)} of ${formatCount(totalAttempts)} succeeded`
        : 'No payments yet',
      series: countSeries,
    },
  ];

  const quickActions = [
    { label: 'API keys', sub: 'Create and revoke', icon: Code2, to: '/dashboard/developer' },
    { label: 'Webhooks', sub: 'Endpoints and retries', icon: Webhook, to: '/dashboard/developer' },
    { label: 'Networks', sub: 'Chains and tokens', icon: Globe, to: '/dashboard/networks' },
    { label: 'Docs', sub: 'Guides and API', icon: BookOpen, to: '/dashboard/guide' },
  ];

  return (
    <div className="home-page">
      <div className="rd-page-header page-header">
        <div className="page-header-titles">
          <h1 className="rd-page-title">Welcome {getUserName()}!</h1>
          <p className="page-subtitle">
            <span className={`mode-dot ${isLive ? 'live' : 'test'}`} />
            {isLive ? 'Live mode' : 'Test mode'}
            {isLive && <span className="live-chip" title="You're operating on mainnet — these are real funds and real customers.">Real funds</span>}
            <span className="dot-sep">·</span>
            <button className="updated-btn" onClick={() => fetchDashboardData(true)} disabled={refreshing}>
              <RefreshCw size={12} className={refreshing ? 'spinning' : ''} />
              {refreshing ? 'Refreshing…' : `Updated ${relativeTime(lastUpdated, nowTick || Date.now())}`}
            </button>
          </p>
        </div>
        <div className="header-actions">
          <div className="period-selector" role="group" aria-label="Date range">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                className={`period-btn ${period === p.key ? 'active' : ''}`}
                onClick={() => handlePeriodChange(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button className="rd-btn rd-btn-secondary" onClick={handleExport} title="Export recent transactions (CSV)">
            <Download size={16} />
            Export
          </button>
          <button className="rd-btn rd-btn-primary" onClick={() => navigate('/dashboard/invoices?action=create')}>
            <Plus size={16} />
            New invoice
          </button>
        </div>
      </div>

      {fetchError && (
        <div className="rd-card rd-empty empty-state dashboard-error">
          <AlertCircle size={48} />
          <h3>Unable to load dashboard data</h3>
          <p>Something went wrong while fetching your data. Please try again.</p>
          <button className="rd-btn rd-btn-primary empty-state-btn" onClick={() => fetchDashboardData()}>
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      )}

      {showSetup && (
        <div className="setup-banner">
          <div className="setup-icon">
            <ShieldCheck size={22} />
          </div>
          <div className="setup-body">
            <h3>Finish setting up MaxelPay</h3>
            <p>
              {setupDone >= 3
                ? 'One step left. Make a test payment to verify your integration end to end.'
                : 'A few quick steps to start accepting crypto payments.'}
            </p>
            <div className="setup-progress">
              <div className="setup-progress-track">
                <div className="setup-progress-fill" style={{ width: `${(setupDone / SETUP_STEPS.length) * 100}%` }} />
              </div>
              <span className="setup-progress-label">
                <strong>{setupDone}</strong> of {SETUP_STEPS.length} complete
              </span>
            </div>
          </div>
          <button className="rd-btn rd-btn-primary setup-cta" onClick={() => navigate('/onboarding')}>
            Finish setup <ArrowRight size={16} />
          </button>
        </div>
      )}

      {(overdueInvoices > 0 || (transactionStats?.not_supported_token || 0) > 0 || pendingPayout > 0) && (
        <div className="attention-strip">
          {overdueInvoices > 0 && (
            <div className="attention-item">
              <AlertTriangle size={16} />
              <span>
                {formatCount(overdueInvoices)} overdue {overdueInvoices === 1 ? 'invoice' : 'invoices'} need attention
              </span>
              <button className="attention-action" onClick={() => navigate('/dashboard/invoices?status=overdue')}>
                View <ArrowRight size={13} />
              </button>
            </div>
          )}
          {/* {(transactionStats?.not_supported_token || 0) > 0 && (
            <button className="attention-item" onClick={() => navigate('/dashboard/transactions?status=not_supported_token')}>
              <AlertTriangle size={16} />
              <span>
                {formatCount(transactionStats.not_supported_token)} not-supported {transactionStats.not_supported_token === 1 ? 'transaction' : 'transactions'} need attention
              </span>
              <span className="attention-action">Review <ArrowRight size={13} /></span>
            </button>
          )} */}
          {pendingPayout > 0 && (
            <button className="attention-item" onClick={() => navigate('/dashboard/payouts')}>
              <Clock size={16} />
              <span>${formatUSD(pendingPayout)} in pending payouts</span>
              <span className="attention-action">View <ArrowRight size={13} /></span>
            </button>
          )}
        </div>
      )}

      {/* Same card as the transactions summary, the balance page and invoices:
          tinted icon tile, label, one figure, one qualifier — plus the revenue
          sparkline, which is the only one of the four with a series behind it. */}
      <div className="kpi-grid">
        {kpis.map((k) => (
          <StatCard
            key={k.key}
            tone={k.tone}
            icon={k.icon}
            label={k.label}
            value={k.value}
            // Shown only where a comparable previous period exists. A card with
            // no base says so rather than printing a fabricated 0%.
            chip={k.delta != null ? `${k.delta < 0 ? '↓' : '↑'} ${Math.abs(k.delta).toFixed(1)}%` : null}
            chipTone={k.delta == null ? undefined : k.delta < 0 ? 'down' : 'up'}
            sub={k.delta != null ? comparisonLabel : k.sub}
            loading={loading}
            chart={k.series?.length ? <MiniSpark points={k.series} tone={k.tone} /> : null}
          />
        ))}
      </div>

      {/* {!showSetup && !hasSucceededPayment && (
        <button className="tip-banner" onClick={() => navigate('/dashboard/guide')}>
          <div className="tip-icon">
            <Star size={18} />
          </div>
          <div className="tip-body">
            <span className="tip-eyebrow">Tip · Get to first payment</span>
            <p>
              Most merchants who complete a test payment in their first session activate within 24 hours.{' '}
              <strong>You're one step away.</strong>
            </p>
            <span className="tip-link">Open quickstart guide <ArrowRight size={14} /></span>
          </div>
        </button>
      )} */}

      {/*
        Revenue over time. The range control is bound to the SAME `period` state
        as the one in the page header, so the two can never disagree -- it is
        one control shown twice, not a second setting.

        Named "Revenue" rather than "Portfolio value": the series is the sum of
        succeeded payments per bucket, which is revenue. There is no holdings
        or valuation series behind this account to plot.
      */}
     

      <div className="rd-grid-2 dash-main-grid">

        <div className="dash-side">
          <div className="rd-card panel balance-panel">
            <div className="panel-body">
              <span className="balance-eyebrow">Available balance</span>
              <span className="balance-big"><span className="cur">$</span>{formatUSD(availableBalance)}</span>
              {/*
                A REVENUE delta, not a balance delta, and labelled that way.
                /payouts/my-balance returns only current figures -- there are no
                historical balance snapshots -- so a balance change cannot be
                derived. Hidden entirely when the preceding 24h had no revenue,
                because a percentage with no base is not a real number.
              */}
              {revenue24h.pct != null && (
                <span
                  className={`balance-delta ${revenue24h.pct >= 0 ? 'is-up' : 'is-down'}`}
                >
                  {revenue24h.pct >= 0 ? '+' : ''}
                  {revenue24h.pct.toFixed(2)}%
                  <em>revenue vs prev 24h</em>
                </span>
              )}
              <div className="balance-rows">
                <div className="balance-row">
                  <span>Pending</span>
                  <span className="balance-num">${formatUSD(pendingPayout)}</span>
                </div>
                <div className="balance-row">
                  <span>Total Revenue</span>
                  <span className="balance-num">${formatUSD(totalRevenue)}</span>
                </div>
              </div>
              <button
                className="rd-btn rd-btn-block rd-btn-secondary btn-block btn-muted"
                onClick={() => navigate('/dashboard/payouts')}
                disabled={availableBalance <= 0}
              >
                <ArrowDownRight size={16} />
                Request payout
              </button>
              {/* {availableBalance <= 0 && (
                <span className="balance-foot">Withdrawals available after your first payment</span>
              )} */}
            </div>
          </div>

          <div className="rd-card panel quick-actions-panel">
            <div className="panel-header">
              <h3 className="rd-section-title">Quick actions</h3>
            </div>
            <div className="panel-body">
              <div className="quick-grid">
                {quickActions.map((qa) => (
                  <button key={qa.label} className="quick-tile" onClick={() => navigate(qa.to)}>
                    <span className="rd-icon-tile quick-tile-icon"><qa.icon size={17} /></span>
                    <span className="quick-tile__text">
                      <span className="quick-tile__label">{qa.label}</span>
                      <span className="quick-tile__sub">{qa.sub}</span>
                    </span>
                    <ChevronRight size={15} className="quick-tile__chev" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="dash-main-col">
          <div className="rd-card panel recent-tx">
            <div className="panel-header rtx-head">
              <span className="rtx-head__icon" aria-hidden="true">
                <Receipt size={18} />
              </span>
              <span className="rtx-head__text">
                <h3 className="rd-section-title">Recent transactions</h3>
                <span className="rtx-head__sub">Latest payments received from your customers</span>
              </span>
              <button className="view-all rd-link" onClick={() => navigate('/dashboard/transactions')}>
                View all <ArrowRight size={14} />
              </button>
            </div>

            <div className="rtx-wrap">
              {loading ? (
                <div className="rtx-skeletons">
                  {Array.from({ length: 5 }, (_, i) => (
                    <div className="rtx-skeleton" key={i}>
                      <Skeleton variant="text" width={70} height={18} />
                      <Skeleton variant="text" width={110} />
                      <Skeleton variant="text" width={60} />
                      <Skeleton variant="text" width={90} />
                      <Skeleton variant="text" width={70} />
                    </div>
                  ))}
                </div>
              ) : recentTransactions.length > 0 ? (
                <RecentTransactionsTable transactions={recentTransactions} />
              ) : (
                <div className="rd-empty empty-state">
                  <div className="rd-empty-icon empty-icon"><Clock size={22} /></div>
                  <h3>No transactions yet</h3>
                  <p>Once you start accepting payments, you'll see them here in real time with full status, on-chain hashes, and reconciliation.</p>
                  {/* <button className="rd-btn rd-btn-secondary empty-state-btn" onClick={() => navigate('/onboarding/payment')}>
                    Try a test payment
                  </button> */}
                </div>
              )}
            </div>

            {/* Counts the whole account, not the five rows above it, so the
                footer says how much is NOT on screen. */}
            {!loading && recentTransactions.length > 0 && (
              <div className="rtx-foot">
                Showing {recentTransactions.length} of {formatCount(transactionStats?.all ?? recentTransactions.length)} transactions
              </div>
            )}
          </div>
        </div>

        
      </div>

       <div className="rd-card panel rev-panel">
        <div className="panel-header">
          <div className="panel-header-left">
            <h3 className="rd-section-title">Activity</h3>
            <span className="rev-panel__total">
              <em>{periodLabel}</em>
            </span>
          </div>
          <div className="period-selector" role="group" aria-label="Chart range">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                className={`period-btn ${period === p.key ? 'active' : ''}`}
                onClick={() => handlePeriodChange(p.key)}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <div className="panel-body rev-panel__body">
          {loading ? (
            <Skeleton variant="text" width="100%" height={230} />
          ) : hasChartData ? (
            <>
              {/* Legend doubles as the period readout, so each curve carries
                  its own total instead of a single ambiguous figure. */}
              <div className="trend-legend">
                {trendSeries.map((sr) => (
                  <span className="trend-legend__item" key={sr.key}>
                    <i style={{ background: `var(--trend-c-${sr.key})` }} />
                    <span className="trend-legend__label">{sr.label}</span>
                    <span className="trend-legend__val">${formatUSD(sr.total)}</span>
                  </span>
                ))}
              </div>
              <TrendChart
                series={trendSeries}
                start={chartStart}
                slot={chartSlot}
                period={period}
              />
            </>
          ) : (
            /* No flat zero line: an empty series drawn as a chart reads as
               "revenue was zero all period", which is a claim the data does
               not support when the period simply has no records. */
            <div className="rd-empty empty-state">
              <div className="rd-empty-icon empty-icon"><Activity size={22} /></div>
              <h3>Nothing in this period</h3>
              <p>Payments, payouts and invoices appear here once you have some in the selected range.</p>
            </div>
          )}
        </div>
      </div>

      <div className="rd-card panel recent-inv recent-inv-full">
        <div className="panel-header">
          <div className="panel-header-left">
            <h3 className="rd-section-title">Recent invoices</h3>
            {!loading && (recentInvoices.length > 0 || overdueInvoiceList.length > 0) && (
              <div className="period-selector inv-filter" role="group" aria-label="Filter invoices">
                <button className={`period-btn ${invoiceFilter === 'all' ? 'active' : ''}`} onClick={() => setInvoiceFilter('all')}>All</button>
                <button className={`period-btn ${invoiceFilter === 'overdue' ? 'active' : ''}`} onClick={() => setInvoiceFilter('overdue')}>Overdue</button>
              </div>
            )}
          </div>
          <button className="view-all rd-link" onClick={() => navigate('/dashboard/invoices')}>
            View all <ArrowRight size={14} />
          </button>
        </div>
        <div className="panel-body">
          {loading ? (
            Array.from({ length: 4 }, (_, i) => (
              <div className="invoice-item" key={i}>
                <div className="invoice-badge skeleton-badge"><Skeleton variant="text" width={40} height={40} /></div>
                <div className="invoice-info">
                  <Skeleton variant="text" width={120} />
                  <Skeleton variant="text" width={90} />
                </div>
                <div className="invoice-meta">
                  <Skeleton variant="text" width={70} />
                </div>
              </div>
            ))
          ) : filteredRecentInvoices.length > 0 ? (
            <div className="rinv-wrap">
              <RecentInvoicesTable
                invoices={filteredRecentInvoices}
                onOpen={(id) => navigate(`/dashboard/invoices/${id}`)}
              />
            </div>
          ) : invoiceFilter === 'overdue' ? (
            <div className="inv-filter-empty">No overdue invoices.</div>
          ) : (
            <div className="rd-empty empty-state">
              <div className="rd-empty-icon empty-icon"><FileText size={22} /></div>
              <h3>No invoices yet</h3>
              <p>Create your first invoice to get started</p>
              <button className="rd-btn rd-btn-primary empty-state-btn" onClick={() => navigate('/dashboard/invoices?action=create')}>
                <Plus size={16} />
                Create invoice
              </button>
            </div>
          )}
        </div>
      </div>

      {upsell.isOpen && (
        <PremiumUpsellModal {...upsell.modalProps} onClose={upsell.close} />
      )}

      <style>{`
        .home-page { padding: 0; }

        /* ── Page header ── */
        .home-page .page-header { margin-bottom: var(--space-6); }
        .home-page .rd-page-title { font-family: var(--font-display); font-size: 30px; font-weight: 700; }
        .home-page .page-subtitle {
          margin: var(--space-1) 0 0;
          display: inline-flex; align-items: center; gap: var(--space-2);
          font-size: 13px; color: var(--t-500);
        }
        .mode-dot { width: 8px; height: 8px; border-radius: var(--r-pill); display: inline-block; flex-shrink: 0; }
        .mode-dot.live { background: var(--ok); box-shadow: 0 0 0 3px var(--ok-soft); }
        .mode-dot.test { background: var(--warn); box-shadow: 0 0 0 3px var(--warn-soft); }
        .dot-sep { color: var(--t-400); }
        .updated-btn {
          display: inline-flex; align-items: center; gap: 5px;
          background: none; border: none; padding: 0; cursor: pointer;
          color: var(--t-400); font-size: 13px; font-weight: 500;font-family: var(--iv-sans);
        }
        .updated-btn:hover:not(:disabled) { color: var(--t-500); text-decoration: underline; }
        .updated-btn:disabled { cursor: default; }
        .updated-btn .spinning { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        .header-actions { display: flex; align-items: center; gap: var(--space-2); flex-wrap: wrap; }

        .period-selector {
          display: inline-flex; background: var(--bg-inset);
          border-radius: var(--r-sm); padding: 3px;
        }
        .period-btn {
          border: none; background: transparent; color: var(--t-400);
          font-size: 13px; font-weight: 600;
          padding: var(--space-2) var(--space-3); border-radius: var(--r-sm); cursor: pointer; transition: color 0.15s; font-family: var(--iv-sans);
        }
        .period-btn:hover { color: var(--t-500); }
        .period-btn.active { background: var(--surface); color: var(--ink); box-shadow: var(--sh-sm); }

        /* ── Live chip (inline, in the subtitle) ── */
        .live-chip {
          display: inline-flex; align-items: center;
          padding: 1px 8px; border-radius: var(--r-pill);
          background: var(--ok-soft); color: var(--ok-ink);
          font-size: 11px; font-weight: 700; letter-spacing: 0.02em;
          cursor: help;
        }

        /* ── Setup banner ── */
        .setup-banner {
          display: flex; align-items: center; gap: var(--space-4);
          padding: var(--space-5); margin-bottom: var(--space-5);
          background: var(--surface); border: 1px solid var(--line);
          border-radius: var(--r-lg); box-shadow: var(--sh-card);
        }
        .setup-icon {
          width: 44px; height: 44px; flex-shrink: 0;
          display: grid; place-items: center; border-radius: var(--r-md);
          background: var(--brand-100); color: var(--brand-700);
        }
        .setup-body { flex: 1; min-width: 0; }
        .setup-body h3 { margin: 0 0 3px; font-size: 15px; font-weight: 700; color: var(--ink); }
        .setup-body p { margin: 0 0 var(--space-3); font-size: 13px; color: var(--t-500); }
        .setup-progress { display: flex; align-items: center; gap: var(--space-3); }
        .setup-progress-track {
          flex: 1; max-width: 260px; height: 6px; border-radius: var(--r-pill);
          background: var(--bg-inset); overflow: hidden;
        }
        .setup-progress-fill { height: 100%; border-radius: var(--r-pill); background: var(--brand-600); transition: width 0.4s; }
        .setup-progress-label { font-size: 13px; color: var(--t-500); white-space: nowrap; }
        .setup-progress-label strong { color: var(--ink); }
        .setup-cta { flex-shrink: 0; }

        /* ── Attention strip ── */
        .attention-strip { display: flex; flex-direction: column; gap: var(--space-2); margin-bottom: var(--space-5); }
        .attention-item {
          display: flex; align-items: center; gap: var(--space-2); width: 100%; text-align: left;
          padding: var(--space-3) var(--space-4); background: var(--warn-soft);
          border: 1px solid var(--warn); border-radius: var(--r-md);
          font-size: 13px; font-weight: 600; color: var(--warn-ink); cursor: pointer; transition: filter 0.13s;
        }
        .attention-item:hover { filter: brightness(0.97); }
        .attention-item svg { flex-shrink: 0; color: var(--warn); }
        /* The message absorbs the slack and wraps inside its own box; the
           action keeps its width. Without these the nowrap action overflowed
           and painted over the message text. */
        // .attention-item > span { flex: 1 1 auto; min-width: 0; }
        .attention-action {
          margin-left: auto; flex-shrink: 0;
          display: inline-flex; align-items: center; gap: var(--space-1);
          background: transparent; border: none; color: var(--warn-ink);
          font-weight: 700; font-size: 12.5px; cursor: pointer; white-space: nowrap;
        }

        /* ── Stat cards (extends .rd-kpi) ── */
        /* Matches the transactions summary's rhythm: four across, 10px gaps,
           compact cards. The old .rd-kpi cell had a left colour stripe and far
           more padding, which is what made this block tower over the page. */
        .home-page .kpi-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 10px;
          margin-bottom: 14px;
        }
        .home-page .kpi-grid .stat-card { padding: 12px 14px; gap: 5px; }
        .home-page .kpi-grid .stat-card--charted { padding-bottom: 28px; }
        .home-page .kpi-grid .stat-card__icon { width: 30px; height: 30px; border-radius: 9px; }
        .home-page .kpi-grid .stat-card__icon svg { width: 16px; height: 16px; }
        .home-page .kpi-grid .stat-card__label { font-size: 12px; }
        /* Revenue totals can run long, so the figure shrinks before it wraps. */
        .home-page .kpi-grid .stat-card__value { font-size: clamp(16px, 1.45vw, 20px); }
        .home-page .kpi-grid .stat-card__sub { font-size: 11px; }
        @media (max-width: 1100px) {
          .home-page .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        }
        @media (max-width: 560px) {
          .home-page .kpi-grid { grid-template-columns: minmax(0, 1fr); }
        }
        .home-page .kpi-cell {
          position: relative; display: flex; flex-direction: column;
          background: var(--surface); border-radius: var(--r-lg);
          box-shadow: var(--sh-card); overflow: hidden;
        }
        /* Left semantic accent bar (3px), mapped per tone. */
        .home-page .kpi-cell::before {
          content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 3px;
          background: var(--kpi-accent, var(--brand-600));
        }
        .home-page .kpi-cell.tone-ok    { --kpi-accent: var(--ok);    --kpi-soft: var(--ok-soft);    --kpi-ink: var(--ok-ink); }
        .home-page .kpi-cell.tone-brand { --kpi-accent: var(--brand-600); --kpi-soft: var(--brand-100); --kpi-ink: var(--brand-700); }
        .home-page .kpi-cell.tone-warn  { --kpi-accent: var(--warn);  --kpi-soft: var(--warn-soft);  --kpi-ink: var(--warn-ink); }
        .home-page .kpi-cell.tone-info  { --kpi-accent: var(--info);  --kpi-soft: var(--info-soft);  --kpi-ink: var(--info-ink); }
        .home-page .kpi-label {
          display: inline-flex; align-items: center; gap: var(--space-2);
          font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase;
          color: var(--t-500);
        }
        /* ── Quick action tiles ── */
        .quick-tile { display: flex; align-items: center; gap: 10px; text-align: left; }
        .quick-tile__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; flex: 1; }
        .quick-tile__label { font-size: 12.5px; font-weight: 600; color: var(--ink, #0e1124); }
        .quick-tile__sub { font-size: 11px; color: var(--t-500, #6c7189); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .quick-tile__chev { flex: none; color: var(--t-400, #9aa0b6); }

        /* ── Recent transactions table ── */
        .rtx-head { display: flex; align-items: center; gap: 11px; }
        .rtx-head__icon {
          flex: none; display: grid; place-items: center;
          width: 34px; height: 34px; border-radius: 10px;
          background: var(--brand-100, #ece9ff); color: var(--brand-600, #6d5df6);
        }
        .rtx-head__text { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
        .rtx-head__sub { font-size: 12px; color: var(--t-500, #6c7189); }
        .rtx-head .view-all { margin-left: auto; }

        
        .rtx-wrap {overflow-x: scroll;height: 400px;}
        /* Table rules ship with components/common/RecentTables. */
        /* Recent invoices table: scrolls sideways on narrow screens like the
           transactions table, but sizes to its rows rather than a fixed height. */
        .rinv-wrap { overflow-x: auto; margin: 0 calc(-1 * var(--space-5)); padding: 0 var(--space-2); }

        .rtx-foot {
          padding: 10px 14px;
          border-top: 1px solid var(--line, #eaebf3);
          font-size: 12px;
          color: var(--t-500, #6c7189);
          font-variant-numeric: tabular-nums;
        }
        .rtx-skeletons { display: flex; flex-direction: column; gap: 14px; padding: 14px; }
        .rtx-skeleton { display: flex; align-items: center; justify-content: space-between; gap: 12px; }

        @media (max-width: 900px) { .rtx-table { min-width: 620px; } }

        /* Sparklines on the overview cards. */
        .mini-spark { width: 100%; height: 100%; display: block; }
        .mini-spark__line { fill: none; stroke-width: 2; vector-effect: non-scaling-stroke; stroke-linecap: round; stroke-linejoin: round; }
        .mini-spark__area { stroke: none; opacity: 0.16; }
        .mini-spark.tone-ok .mini-spark__line    { stroke: var(--ok, #0fb981); }
        .mini-spark.tone-ok .mini-spark__area    { fill: var(--ok, #0fb981); }
        .mini-spark.tone-info .mini-spark__line  { stroke: var(--info, #3b9eff); }
        .mini-spark.tone-info .mini-spark__area  { fill: var(--info, #3b9eff); }
        .mini-spark.tone-warn .mini-spark__line  { stroke: var(--warn, #f5a524); }
        .mini-spark.tone-warn .mini-spark__area  { fill: var(--warn, #f5a524); }
        .mini-spark.tone-brand .mini-spark__line { stroke: var(--brand-600, #6d5df6); }
        .mini-spark.tone-brand .mini-spark__area { fill: var(--brand-600, #6d5df6); }

        /* Five across now, not four. */
        .home-page .kpi-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); }
        @media (max-width: 1400px) { .home-page .kpi-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
        @media (max-width: 900px)  { .home-page .kpi-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
        @media (max-width: 560px)  { .home-page .kpi-grid { grid-template-columns: minmax(0, 1fr); } }

        /* RevenueChart still renders this; the card supplies the box around it. */
        .kpi-spark-svg { width: 100%; height: 100%; display: block; }

        /* ── Tip banner ── */
        .tip-banner {
          display: flex; align-items: flex-start; gap: var(--space-3); width: 100%; text-align: left;
          padding: var(--space-5); margin-bottom: var(--space-5); cursor: pointer;
          background: var(--brand-100); border: 1px solid var(--line);
          border-radius: var(--r-lg); transition: border-color 0.13s, box-shadow 0.13s;
        }
        .tip-banner:hover { border-color: var(--brand-600); box-shadow: var(--sh-card); }
        .tip-icon {
          width: 36px; height: 36px; flex-shrink: 0;
          display: grid; place-items: center; border-radius: var(--r-md);
          background: var(--brand-600); color: var(--t-on-dark);
        }
        .tip-body { min-width: 0; }
        .tip-eyebrow {
          font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
          text-transform: uppercase; color: var(--brand-700);
        }
        .tip-body p { margin: var(--space-1) 0 var(--space-2); font-size: 13px; color: var(--t-500); }
        .tip-body p strong { color: var(--ink); }
        .tip-link {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 13px; font-weight: 600; color: var(--brand-700);
        }

        /* ── Main grid + panels ── */
        /* Each column sizes to its own content.
           This was align-items: stretch, with .recent-tx set to flex:1 1 auto
           and a hard-coded height:300px, so the left panel grew to match the
           balance + quick-actions rail. That made sense when the rail was the
           taller of the two and the feed was a short list. The feed is a table
           now and is usually the taller one, so stretching only ever padded it
           with the empty band under the last row. */
        /* Both columns share the row height, so the two boxes end level.
           Earlier this was align-items: start, which sized each column to its
           own content and left the shorter one visibly short beside the other.
           Stretching is only safe now because the slack has somewhere to go:
           the table card pins its footer to the bottom and the rail lets its
           last panel absorb the remainder, so neither grows a blank band. */
        .home-page .dash-main-grid { align-items: stretch; margin-bottom: 0; }
        .home-page .recent-inv-full { margin-top: var(--space-5); }
        .dash-main-col { display: flex; flex-direction: column; gap: var(--space-5); }
        .dash-main-col .recent-tx { flex: 1; display: flex; flex-direction: column; }
        /* Pinned to the bottom of the card, so any extra height falls between
           the last row and the footer rather than below everything. */
        .rtx-foot { margin-top: auto; }
        /* The rail's last panel takes the leftover instead of the gap between
           panels stretching. */
        .dash-side > :last-child { flex: 1; display: flex; flex-direction: column; }
        .dash-side > :last-child .panel-body { flex: 1; display: flex; flex-direction: column; }
        .dash-side > :last-child .quick-grid { flex: 1; align-content: stretch; }
        /* The empty and loading states still want a floor, or a card with no
           rows collapses to just its header. */
        .recent-tx .empty-state, .recent-tx .loading-state { min-height: 220px; }
        .dash-side { display: flex; flex-direction: column; gap: var(--space-5); }

        .home-page .panel { overflow: hidden; }
        
        .panel-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: var(--space-4) var(--space-5); border-bottom: 1px solid var(--line);
        }
        .panel-body { padding: var(--space-2) var(--space-5) var(--space-4); }

        /* ---- Revenue over time ------------------------------------------- */

        .rev-panel__total {
          display: inline-flex;
          align-items: baseline;
          gap: 8px;
          font-size: 18px;
          font-weight: 600;
          letter-spacing: -0.02em;
          color: var(--ink);
        }

        .rev-panel__total em {
          font-style: normal;
          font-size: 11.5px;
          font-weight: 500;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--t-400);
        }

        .rev-panel__body {
          padding: 10px 16px 14px;
        }
        /* One place for the series colours, so the SVG, the legend and the
           tooltip can never disagree. Violet is the app accent; rose and amber
           are chosen for hue separation that survives both themes rather than
           relying on lightness alone. */
        .trend-chart,
        .trend-legend {
          --trend-c-payments: var(--accent, #6366f1);
          --trend-c-payouts: #f43f5e;
          --trend-c-invoices: #f59e0b;
        }

        .trend-legend {
          display: flex;
          flex-wrap: wrap;
          gap: 6px 18px;
          margin-bottom: 6px;
        }

        .trend-legend__item {
          display: inline-flex;
          align-items: center;
          gap: 7px;
          font-size: 12.5px;
          color: var(--t-400);
        }

        .trend-legend__item i {
          width: 9px;
          height: 9px;
          border-radius: 50%;
          flex-shrink: 0;
        }

        .trend-legend__val {
          font-weight: 600;
          color: var(--ink);
        }

        /* position: relative anchors the hover tooltip, which is placed as a
           percentage of the SVG viewBox. */
        .trend-chart {
          position: relative;
          width: 100%;
        }

        .trend-chart__svg {
          display: block;
          width: 100%;
          height: auto;
          overflow: visible;
        }

        .trend-chart__grid {
          stroke: var(--line);
          stroke-width: 1;
          stroke-dasharray: 4 8;
          /* Hairlines must not thicken as the SVG scales up to the card. */
          vector-effect: non-scaling-stroke;
        }

        .trend-chart__ytick,
        .trend-chart__xtick {
          fill: var(--t-400);
          /* viewBox units: the box is ~2x wider now, so this reads at about the
             same size on screen as 8px did before. */
          font-size: 15px;
          /* Inherit Poppins rather than the SVG default serif. */
          font-family: inherit;
        }

        .trend-chart__area {
          animation: trend-area-in 520ms
            var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
        }

        @keyframes trend-area-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        /* --trend-c is set per <g> in the JSX, so one rule styles all three
           curves and the colour stays with the series. */
        .trend-chart__line {
          fill: none;
          stroke: var(--trend-c);
          stroke-width: 2.25;
          stroke-linecap: round;
          stroke-linejoin: round;
          vector-effect: non-scaling-stroke;
          /* Draws itself in. The dash length only has to EXCEED the path
             length, and the plot is 1500 units wide now — at the old 2600 a
             long series would stop drawing partway and leave the curve clipped. */
          stroke-dasharray: 5200;
          animation: trend-line-draw 1000ms
            var(--mx-ease-glide, cubic-bezier(0.22, 0.61, 0.36, 1)) both;
        }

        /* Staggered so three curves resolve one after another instead of
           racing each other into the same frame. */
        .trend-chart__svg > g:nth-of-type(1) .trend-chart__line { animation-delay: 0ms; }
        .trend-chart__svg > g:nth-of-type(2) .trend-chart__line { animation-delay: 110ms; }
        .trend-chart__svg > g:nth-of-type(3) .trend-chart__line { animation-delay: 200ms; }

        @keyframes trend-line-draw {
          from { stroke-dashoffset: 5200; }
          to   { stroke-dashoffset: 0; }
        }

        .trend-chart__cursor {
          stroke: var(--t-400);
          stroke-width: 1;
          stroke-dasharray: 3 3;
          opacity: 0.6;
          vector-effect: non-scaling-stroke;
        }

        .trend-chart__dot {
          fill: var(--trend-c);
          stroke: var(--surface, var(--bg-primary, #fff));
          stroke-width: 2;
          /* The ring stays a constant 2px however the SVG is scaled. */
          vector-effect: non-scaling-stroke;
        }

        /* translate(-50%, -100%) with a bottom offset lifts the card clear of
           the cursor line. pointer-events: none stops it stealing the mousemove
           that positions it, which would make it flicker. */
        .trend-chart__tip {
          position: absolute;
          bottom: 34px;
          display: flex;
          flex-direction: column;
          gap: 3px;
          padding: 8px 11px;
          border: 1px solid var(--line);
          border-radius: var(--r-sm, 8px);
          background: var(--surface, var(--bg-primary, #fff));
          box-shadow: 0 8px 24px rgba(15, 23, 42, 0.16);
          pointer-events: none;
          white-space: nowrap;
          z-index: 2;
        }

        /* Side of the hover line the card opens on, with a 12px gap so it
           never covers the line or the dots on it. */
        .trend-chart__tip--left  { transform: translateX(calc(-100% - 12px)); }
        .trend-chart__tip--right { transform: translateX(12px); }

        .trend-chart__tip-when {
          font-size: 10.5px;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--t-400);
        }

        .trend-chart__tip-row {
          display: grid;
          grid-template-columns: 9px 1fr auto;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--t-500);
        }

        .trend-chart__tip-row i {
          width: 9px;
          height: 9px;
          border-radius: 50%;
        }

        .trend-chart__tip-row b {
          font-weight: 600;
          color: var(--ink);
        }

        /* ---- Balance panel: 24h revenue delta ---------------------------- */

        .balance-delta {
          display: inline-flex;
          align-items: baseline;
          gap: 6px;
          margin-top: 4px;
          font-size: 13px;
          font-weight: 600;
        }

        .balance-delta.is-up { color: var(--ok, var(--success, #15803d)); }
        .balance-delta.is-down { color: var(--err, var(--danger, #b91c1c)); }

        .balance-delta em {
          font-style: normal;
          font-size: 11px;
          font-weight: 500;
          color: var(--t-400);
        }

        @media (prefers-reduced-motion: reduce) {
          .trend-chart__area,
          .trend-chart__line {
            animation: none;
          }

          /* Without the animation there is no end state to hold, so the dash
             array has to be cleared explicitly or every line stays invisible. */
          .trend-chart__line {
            stroke-dasharray: none;
          }
        }

        /* ---- Recent transactions row -------------------------------------
         *
         * Four columns rather than the old three, because the previous layout
         * stacked status, token amount, USD amount and date into one right-hand
         * cell with a 1px gap between them. Giving status its own column stops
         * a badge and a figure competing for the same edge.
         *
         * Renamed off .transaction-* on purpose: that name is also styled in
         * index.css (line 2078) and in responsive/pages/Home.css, so the row
         * was inheriting from three places at once. .recent-tx-row is scoped to
         * this section and inherits from none of them.
         */
        .recent-tx-row {
          display: grid;
          grid-template-columns: 38px minmax(0, 1fr) auto auto;
          align-items: center;
          column-gap: var(--space-3);
          padding: 13px 20px;
          margin: 0 calc(-1 * var(--space-5));
          border-bottom: 1px solid var(--line);
          transition: background 0.13s ease;
        }

        .recent-tx-row:last-child {
          border-bottom: none;
        }

        .recent-tx-row:hover {
          background: var(--surface-2);
        }

        .recent-tx-row__logo {
          display: grid;
          place-items: center;
          width: 38px;
          height: 38px;
        }

        /* min-width: 0 is what lets the ellipsis below actually engage: a grid
           item defaults to min-width auto and will not shrink under its
           content, so the row would widen instead of truncating. */
        .recent-tx-row__ident {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .recent-tx-row__title {
          font-size: 14px;
          font-weight: 600;
          color: var(--ink);
          letter-spacing: -0.01em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* Was var(--font-mono) showing the currency a second time, directly
           under the title that already named it. Now the network and timestamp,
           in the UI face. */
        .recent-tx-row__meta {
          font-size: 12px;
          color: var(--t-400);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .recent-tx-row__figures {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
          /* Right-aligned, and that is load-bearing here: Poppins ships no
             tabular-figure feature, so font-variant-numeric: tabular-nums is a
             no-op and digit widths vary. Aligning the trailing edge keeps the
             column tidy without a monospace face. */
          text-align: right;
        }

        .recent-tx-row__amount {
          font-size: 14.5px;
          font-weight: 600;
          color: var(--ink);
          letter-spacing: -0.01em;
          white-space: nowrap;
        }

        .recent-tx-row__sub {
          font-size: 12px;
          color: var(--t-400);
          white-space: nowrap;
        }

        .recent-tx-row__status {
          text-transform: capitalize;
          justify-self: end;
        }
        /* Let the title yield first, and never let "View all" be squeezed into
           the All/Overdue toggle. Both are no-ops while the row fits. */
        .panel-header .rd-section-title { min-width: 0; margin-bottom: 0px;}
        .view-all {
          display: inline-flex; align-items: center; gap: var(--space-1);
          background: none; border: none; flex-shrink: 0; white-space: nowrap;
        }
        .view-all.rd-link:hover { text-decoration: none; color: var(--brand-700); }

        /* Transaction rows */
        /* The logo brings its own artwork, so drop the tinted glyph chrome. */

        /* Balance panel */
        .balance-panel .panel-body { padding: 16px 18px; }
        .balance-eyebrow {
          display: block; font-size: 11px; font-weight: 700; letter-spacing: 0.05em;
          text-transform: uppercase; color: var(--t-400); margin-bottom: var(--space-2);
        }
        .balance-big {
          display: block; font-family: var(--font-display);
          font-size: 30px; font-weight: 700; letter-spacing: -0.02em;
          color: var(--ink); font-variant-numeric: tabular-nums; margin-bottom: var(--space-4);
        }
        .balance-big .cur { font-size: 20px; font-weight: 600; color: var(--t-500); margin-right: 1px; }
        .balance-rows { display: flex; flex-direction: column; gap: 7px; margin-bottom: 12px; }
        .balance-row { display: flex; justify-content: space-between; align-items: baseline; font-size: 13px; color: var(--t-500); }
        .balance-num { font-family: var(--font-mono); font-weight: 600; color: var(--ink); font-variant-numeric: tabular-nums; }
        .balance-foot { display: block; font-size: 12px; color: var(--t-400); text-align: center; margin-top: var(--space-3); }

        .home-page .balance-panel .btn-block { margin-bottom: 0; }

        /* Quick actions */
        .quick-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .quick-tile {
          display: flex; align-items: center; gap: 10px;
          padding: 10px 12px; border-radius: var(--r-md);
          background: var(--surface); border: 1px solid var(--line);
          font-size: 13px; font-weight: 600; color: var(--ink); cursor: pointer;
          transition: transform 0.13s, border-color 0.13s, box-shadow 0.13s;
          font-family: var(--iv-sans);
        }
        .quick-tile:hover { transform: translateY(-2px); border-color: var(--brand-400); box-shadow: var(--sh-hover); }
        .quick-tile-icon { width: 30px; height: 30px; flex-shrink: 0; }

        /* Recent invoices */
        .invoice-item {
          display: flex; align-items: center; gap: var(--space-3);
          padding: 14px 20px; margin: 0 calc(-1 * var(--space-5));
          border-bottom: 1px solid var(--line); cursor: pointer; transition: background 0.13s;
        }
        .invoice-item:last-child { border-bottom: none; }
        .invoice-item:hover { background: var(--surface-2); }
        .invoice-badge {
          width: 40px; height: 40px; border-radius: var(--r-md); flex-shrink: 0;
          display: grid; place-items: center;
          color: var(--brand-700); background: var(--brand-100);
        }
        .invoice-badge.skeleton-badge { background: transparent; }
        /* Left column: name over invoice id. min-width: 0 lets it shrink, and
           the name ellipsises rather than spilling over the column to its
           right. In practice there is ~700px here, so a 40-character name
           never truncates. */
        .invoice-info {
          display: flex; flex-direction: column; gap: 2px;
          flex: 1 1 auto; min-width: 0; overflow: hidden;
        }
        .invoice-customer {
          font-size: 14px; font-weight: 700; color: var(--ink);
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .invoice-id { font-size: 11.5px; color: var(--t-400); font-family: var(--font-mono); white-space: nowrap; }
        /* Right column: status, amount, date. width and margin-left are stated
           explicitly so an unscoped .invoice-meta width:100% elsewhere in the
           cascade can never claim the row again. */
        .invoice-meta {
          display: flex; align-items: center; gap: var(--space-3);
          flex: 0 0 auto; width: auto; margin-left: auto;
        }
        .home-page .invoice-status-badge { font-size: 11px; padding: 2px 8px; text-transform: capitalize; }
        .invoice-amount { font-family: var(--iv-sans); font-size: 15px; font-weight: 700; color: var(--ink); font-variant-numeric: tabular-nums; }
        .invoice-date { font-size: 12px; color: var(--t-500); }

        /* Recent-invoices quick filter */
        .panel-header-left { display: flex; align-items: center; gap: var(--space-3); min-width: 0; }
        .inv-filter { flex-shrink: 0; }
        .inv-filter-empty { padding: var(--space-6) var(--space-5); text-align: center; color: var(--t-500); font-size: 13px; }

        /* Empty / loading states (extend .rd-empty) */
        .home-page .empty-state { padding: var(--space-8) var(--space-6); }
        .home-page .recent-tx .empty-state,
        .home-page .recent-inv .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; }
        .home-page .empty-state-btn { margin-top: var(--space-4); }
        .dashboard-error { margin-bottom: var(--space-5); }
        .dashboard-error > svg { color: var(--err); margin-bottom: var(--space-2); }

        .loading-state {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: var(--space-6) var(--space-4); gap: var(--space-3); color: var(--t-500); font-size: 13px;
        }
        .spinner-small {
          width: 24px; height: 24px; border: 2px solid var(--line);
          border-top-color: var(--brand-600); border-radius: var(--r-pill); animation: spin 1s linear infinite;
        }

        @media (prefers-reduced-motion: reduce) {
          .home-page .quick-tile,
          .home-page .updated-btn .spinning,
          .home-page .setup-progress-fill,
          .home-page .spinner-small { transition: none; animation: none; }
          .home-page .quick-tile:hover { transform: none; }
        }
        @media (max-width: 768px){
           .panel-body.rev-panel__body{
                padding-right: 20px;   
           }
        }
        @media (max-width: 576px) {    
            .panel-header-left {display: inline-block;margin-bottom:10px;}
            .home-page .panel{overflow: scroll;}
            .rtx-wrap {height:auto;}
            .rev-panel .panel-header {
                display: inline-grid;
            }
            .dash-main-col{margin-top:20px;}
        }
      `}</style>
    </div>
  );
};

export default Home;
